import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';
import {useTranslation} from 'react-i18next';
import {useIsMobile} from '../hooks/use-is-mobile';
import ReactSignatureCanvas from 'react-signature-canvas';
import {Button} from '../button';
import {cn} from '../lib/cn';

const SIGNATURE_PROPS = {
  minWidth: 1.5,
  maxWidth: 2,
  dotSize: 2,
  velocityFilterWeight: 0.7,
  throttle: 16,
};

const PEN_COLOR_VAR = '--chekin-blue-900';
const PEN_COLOR_FALLBACK = '#161643';

const TARGET_WIDTH = 520;
const TARGET_HEIGHT = 300;
const ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT;
const MIN_HEIGHT = 250;
const RESERVED_BELOW = 200;

type SignatureCanvasProps = {
  onClear?: () => void;
  onEnable?: () => void;
  onEnd?: () => void;
  className?: string;
  enabled?: boolean;
};

export type SignatureCanvasRef = Pick<
  ReactSignatureCanvas,
  'clear' | 'isEmpty' | 'toDataURL'
> & {
  fromDataURL: (
    ...args: Parameters<ReactSignatureCanvas['fromDataURL']>
  ) => ReturnType<ReactSignatureCanvas['fromDataURL']> | undefined;
  getCanvas: () => ReturnType<ReactSignatureCanvas['getCanvas']> | undefined;
  toTrimmedDataURL: (type?: string, encoderOptions?: number, padding?: number) => string;
};

function getCanvasPixelPadding(canvas: HTMLCanvasElement, padding: number) {
  if (!Number.isFinite(padding) || padding <= 0) return 0;

  const cssWidth = canvas.getBoundingClientRect().width;
  const scale = cssWidth > 0 ? canvas.width / cssWidth : 1;

  return Math.round(padding * scale);
}

function getTrimmedCanvas(
  canvas: HTMLCanvasElement,
  padding = 10,
): HTMLCanvasElement | null {
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const {width, height} = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const {data} = imageData;

  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let hasContent = false;

  // Find bounding box of non-transparent pixels
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 0) {
        hasContent = true;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!hasContent) return null;

  // Add padding
  const pixelPadding = getCanvasPixelPadding(canvas, padding);
  minX = Math.max(0, minX - pixelPadding);
  minY = Math.max(0, minY - pixelPadding);
  maxX = Math.min(width - 1, maxX + pixelPadding);
  maxY = Math.min(height - 1, maxY + pixelPadding);

  const trimmedWidth = maxX - minX + 1;
  const trimmedHeight = maxY - minY + 1;

  // Create trimmed canvas
  const trimmedCanvas = document.createElement('canvas');
  trimmedCanvas.width = trimmedWidth;
  trimmedCanvas.height = trimmedHeight;

  const trimmedCtx = trimmedCanvas.getContext('2d');
  if (!trimmedCtx) return null;

  trimmedCtx.drawImage(
    canvas,
    minX,
    minY,
    trimmedWidth,
    trimmedHeight,
    0,
    0,
    trimmedWidth,
    trimmedHeight,
  );

  return trimmedCanvas;
}

const SignatureCanvas = forwardRef<SignatureCanvasRef, SignatureCanvasProps>(
  ({onClear, onEnd, onEnable, className, enabled}, ref) => {
    const {t} = useTranslation();
    const isMobile = useIsMobile();
    const canvasRef = useRef<ReactSignatureCanvas | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [size, setSize] = useState({width: TARGET_WIDTH, height: TARGET_HEIGHT});
    const [hasSignature, setHasSignature] = useState(false);
    const [penColor, setPenColor] = useState(PEN_COLOR_FALLBACK);

    const recalculateSize = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      const ownerWindow = container.ownerDocument.defaultView ?? window;

      const width = Math.min(container.clientWidth, TARGET_WIDTH);
      const aspectHeight = Math.round(width / ASPECT_RATIO);

      const availableHeight =
        ownerWindow.innerHeight - container.getBoundingClientRect().top - RESERVED_BELOW;

      // Keep the aspect-ratio height, but never below MIN_HEIGHT (e.g. narrow
      // mobile widths) and never taller than the space the viewport can give the
      // dialog.
      const height = Math.max(
        MIN_HEIGHT,
        Math.min(aspectHeight, Math.floor(availableHeight)),
      );

      setSize(prev => {
        if (prev.width === width && prev.height === height) {
          return prev;
        }
        return {width, height};
      });
    }, []);

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const resolved = getComputedStyle(container).getPropertyValue(PEN_COLOR_VAR).trim();
      if (resolved) setPenColor(resolved);
    }, []);

    useEffect(() => {
      const canvas = canvasRef.current?.getCanvas();
      if (!canvas) return;

      const ownerDocument = canvas.ownerDocument;
      if (typeof document === 'undefined' || ownerDocument === document) return;

      const forwardRelease = () => {
        document.dispatchEvent(
          new MouseEvent('mouseup', {bubbles: true, button: 0, which: 1}),
        );
      };

      ownerDocument.addEventListener('mouseup', forwardRelease);
      return () => ownerDocument.removeEventListener('mouseup', forwardRelease);
    }, []);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      recalculateSize();

      const ownerWindow = container.ownerDocument.defaultView ?? window;
      const observer = new ResizeObserver(() => recalculateSize());
      observer.observe(container);
      ownerWindow.addEventListener('resize', recalculateSize);

      return () => {
        observer.disconnect();
        ownerWindow.removeEventListener('resize', recalculateSize);
      };
    }, [recalculateSize]);

    // Scale canvas for HiDPI/Retina displays
    useLayoutEffect(() => {
      const canvas = canvasRef.current?.getCanvas();
      if (!canvas) return;

      const ratio = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set actual canvas size in memory (scaled up)
      canvas.width = size.width * ratio;
      canvas.height = size.height * ratio;

      // Scale the context to match
      ctx.scale(ratio, ratio);

      // Set display size via CSS
      canvas.style.width = `${size.width}px`;
      canvas.style.height = `${size.height}px`;
    }, [size, enabled]);

    const handleEnd = useCallback(() => {
      const isEmpty = canvasRef.current?.isEmpty() ?? true;
      setHasSignature(!isEmpty);
      onEnd?.();
    }, [onEnd]);

    const handleClear = useCallback(() => {
      canvasRef.current?.clear();
      setHasSignature(false);
      onClear?.();
    }, [onClear]);

    useImperativeHandle(ref, () => ({
      clear: () => {
        canvasRef.current?.clear();
        setHasSignature(false);
      },
      isEmpty: () => canvasRef.current?.isEmpty() ?? true,
      fromDataURL: (...args) => canvasRef.current?.fromDataURL(...args),
      toDataURL: (type?: string, encoderOptions?: number) =>
        canvasRef.current?.toDataURL(type, encoderOptions) ?? '',
      toTrimmedDataURL: (type = 'image/png', encoderOptions?: number, padding = 10) => {
        const canvas = canvasRef.current?.getCanvas();
        if (!canvas) return '';

        const trimmed = getTrimmedCanvas(canvas, padding);
        if (!trimmed) return '';

        return trimmed.toDataURL(type, encoderOptions);
      },
      getCanvas: (...args) => canvasRef.current?.getCanvas(...args),
    }));

    return (
      <div
        ref={containerRef}
        className={cn('relative flex w-full flex-col items-center', className)}
      >
        <div
          className={cn(
            'relative overflow-hidden rounded-xl border-2',
            'border-[var(--signature-canvas-border)] bg-[var(--signature-canvas-bg)]',
          )}
          style={{width: size.width, height: size.height}}
        >
          <ReactSignatureCanvas
            ref={canvasRef}
            data-testid="canvas"
            canvasProps={{
              width: size.width,
              height: size.height,
              style: {width: size.width, height: size.height},
            }}
            onEnd={handleEnd}
            penColor={penColor}
            {...SIGNATURE_PROPS}
          />
          {!enabled && (
            <button
              type="button"
              className={cn(
                'absolute inset-0 flex cursor-pointer items-center justify-center',
                'bg-transparent',
              )}
              onClick={onEnable}
              data-testid="signature-placeholder"
            >
              <span
                className={cn(
                  'max-w-[200px] break-words text-center text-lg font-medium',
                  'text-[var(--signature-canvas-placeholder-text)] opacity-60',
                )}
              >
                {isMobile ? t('tap_here_to_sign') : t('click_here_to_sign')}
              </span>
            </button>
          )}
        </div>
        <div
          className={cn('text-right', {
            invisible: !enabled || !hasSignature,
          })}
          style={{width: size.width}}
        >
          <Button
            variant="link"
            className={cn(
              'ml-auto mt-2 h-[30px] min-w-[70px] select-none rounded-sm px-2 py-0.5 text-right',
              'text-xs uppercase hover:opacity-[0.88] active:opacity-100',
            )}
            onClick={handleClear}
          >
            {t('clear')}
          </Button>
        </div>
      </div>
    );
  },
);

SignatureCanvas.displayName = 'SignatureCanvas';

export {SignatureCanvas};
